export type DiffKind = 'same' | 'added' | 'removed';
export interface DiffSegment { kind: DiffKind; text: string }

// Word runs (with an internal apostrophe for contractions), whitespace runs and single punctuation
// characters each tokenise on their own, so a comma or full stop change is a one token diff.
const TOKEN_RE = /\s+|[A-Za-z0-9]+(?:['’][A-Za-z0-9]+)*|[^\sA-Za-z0-9]/g;

function tokenize(text: string): string[] {
  return text.match(TOKEN_RE) ?? [];
}

// LCS over n*m tokens; a full paragraph pair rarely nears this, but a long story pasted whole
// would make the O(n*m) table too big, so past the cap we give up on aligning it token by token.
const TOKEN_PRODUCT_CAP = 250_000;

/** Word level diff between before and after: tokenise, take the longest common subsequence of tokens, merge adjacent same kind tokens into segments. */
export function wordDiff(before: string, after: string): DiffSegment[] {
  const a = tokenize(before);
  const b = tokenize(after);
  if (a.length * b.length > TOKEN_PRODUCT_CAP) return fallback(before, after);
  return merge(alignTokens(a, b));
}

function fallback(before: string, after: string): DiffSegment[] {
  const segments: DiffSegment[] = [];
  if (before) segments.push({ kind: 'removed', text: before });
  if (after) segments.push({ kind: 'added', text: after });
  return segments;
}

function alignTokens(a: string[], b: string[]): DiffSegment[] {
  const n = a.length;
  const m = b.length;
  const table: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      table[i][j] = a[i] === b[j] ? table[i + 1][j + 1] + 1 : Math.max(table[i + 1][j], table[i][j + 1]);
    }
  }
  const out: DiffSegment[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ kind: 'same', text: a[i] });
      i++; j++;
    } else if (table[i + 1][j] >= table[i][j + 1]) {
      out.push({ kind: 'removed', text: a[i] });
      i++;
    } else {
      out.push({ kind: 'added', text: b[j] });
      j++;
    }
  }
  while (i < n) { out.push({ kind: 'removed', text: a[i] }); i++; }
  while (j < m) { out.push({ kind: 'added', text: b[j] }); j++; }
  return out;
}

function merge(tokens: DiffSegment[]): DiffSegment[] {
  const out: DiffSegment[] = [];
  for (const t of tokens) {
    const last = out[out.length - 1];
    if (last && last.kind === t.kind) last.text += t.text;
    else out.push({ ...t });
  }
  return out;
}
